/**
 * @typedef {function(): boolean} _Predicate
 * @property {function(_Predicate): _Predicate} and
 * @property {function(_Predicate): _Predicate} or
 */

/**
 * @typedef {_Predicate} _BadgeCompositionRuleCondition
 */

/**
 * @typedef {function(): _BadgeCompositionRaw} _BadgeCompositionRulePartial
 */

/**
 * @param {function(): boolean} predicateFn
 * @returns {_Predicate|function(): boolean}
 */
export const makePredicate = predicateFn => {
  const enhancedPredicateFn = () => predicateFn();
  enhancedPredicateFn.and = otherPredicate => makePredicate(() => predicateFn() && otherPredicate());
  enhancedPredicateFn.or = otherPredicate => makePredicate(() => predicateFn() || otherPredicate());
  return enhancedPredicateFn;
};

/**
 * @param {_Predicate} fn
 * @returns {_Predicate}
 */
export const not = fn => makePredicate(() => !(fn()));

/**
 * @param {string} code
 * @returns {BadgeCompositionRule}
 */
export const rule = code => new BadgeCompositionRule(code);

/**
 * Contains a single rule for badge composition.
 */
export class BadgeCompositionRule {

  /** @type {string} */
  code;

  /** @type {_BadgeCompositionRuleCondition[]} */
  conditions = [];

  /** @type {_BadgeCompositionRulePartial[]} */
  partials = [];

  /**
   * @param {string} code
   */
  constructor(code) {
    this.code = code;
  }

  /**
   * @param {_BadgeCompositionRuleCondition} condition
   * @returns {BadgeCompositionRule}
   */
  when(condition) {
    this.conditions.push(condition);
    return this;
  }

  /**
   * @returns {BadgeCompositionRule}
   */
  byDefault() {
    return this;
  }

  /**
   * @param {_BadgeCompositionRulePartial} partial
   * @returns {BadgeCompositionRule}
   */
  set(partial) {
    this.partials.push(partial);
    return this;
  }

  /**
   * @param {BadgeComposition} composition
   * @returns {object|void}
   */
  applyOn(composition) {
    if (this.conditions.length === 0 || this.conditions.every(condition => condition())) {
      const part = this.partials.map(partialFn => partialFn()).reduce((res, partial) => ({ ...res, ...partial }), {});
      this.applyPartial(composition, part);
      return part;
    }
  }

  /**
   * @param {any} main
   * @param {object} part
   */
  applyPartial(main, part) {
    Object.entries(part).forEach(([key, val]) => {
      if (val && (typeof val === 'object')) {
        main[key] = main[key] || {};
        this.applyPartial(main[key], val);
      } else {
        main[key] = val;
      }
    });
  }

}
